import React from "react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { formatDateTimeDisplay } from "../../lib/datetime";

const DASHBOARD_TYPOGRAPHY = {
  body: "text-sm font-medium text-white",
  meta: "text-sm text-white/72",
};

const NewsTab = ({ newsEvents, newsLoading, fetchNewsEvents, symbol, appTimeZone }) => (
  <div className="h-full overflow-auto">
    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <h3 className="text-sm font-semibold uppercase tracking-[0.18em] text-white/70">Upcoming Market Events</h3>
      <Button onClick={fetchNewsEvents} disabled={newsLoading} className="bg-white/10 text-white hover:bg-white/20">
        {newsLoading ? "Refreshing..." : "Refresh News"}
      </Button>
    </div>

    {newsLoading ? (
      <div className="text-center py-8 text-white/70">
        Loading upcoming events...
      </div>
    ) : newsEvents.length > 0 ? (
      <div className="space-y-3">
        {newsEvents.map((event) => (
          <Card key={event.id} className="bg-white/5 border-white/10">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className={DASHBOARD_TYPOGRAPHY.body}>{event.title}</div>
                  <div className={DASHBOARD_TYPOGRAPHY.meta}>
                    {formatDateTimeDisplay(event.starts_at, appTimeZone, { timeZoneName: "short" })}
                  </div>
                </div>
                <Badge
                  className={
                    event.impact === "high"
                      ? "bg-red-500/20 text-red-300 border-red-500/30"
                      : event.impact === "medium"
                        ? "bg-amber-500/20 text-amber-200 border-amber-500/30"
                        : "bg-cyan-500/20 text-cyan-200 border-cyan-500/30"
                  }
                >
                  {(event.impact || "medium").toUpperCase()} IMPACT
                </Badge>
              </div>

              {(event.affected_symbols || []).length ? (
                <div className="flex flex-wrap gap-2">
                  {event.affected_symbols.map((affectedSymbol) => (
                    <Badge key={`${event.id}-${affectedSymbol}`} variant="secondary" className="bg-white/10 text-white border-white/20">
                      {affectedSymbol}
                    </Badge>
                  ))}
                </div>
              ) : null}

              {event.market_impact ? (
                <div>
                  <div className={`${DASHBOARD_TYPOGRAPHY.body} mb-1`}>Expected Market Impact</div>
                  <p className="text-sm leading-6 text-white/80">{event.market_impact}</p>
                </div>
              ) : null}

              {event.notes ? (
                <div className="text-sm leading-6 text-white/60">{event.notes}</div>
              ) : null}

              <div className="flex flex-wrap items-center gap-3 text-xs text-white/70">
                {event.source ? <span>Source: {event.source}</span> : null}
                {typeof event.minutes_until === "number" ? (
                  <span>{event.minutes_until < 60 ? `${event.minutes_until} min` : `${Math.floor(event.minutes_until / 60)}h ${event.minutes_until % 60}m`} away</span>
                ) : null}
                {event.source_url ? (
                  <a href={event.source_url} target="_blank" rel="noreferrer" className="text-blue-300 hover:text-blue-200">
                    Open source
                  </a>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    ) : (
      <div className="text-center py-8 text-white/70">
        No upcoming events are configured for {symbol}.
      </div>
    )}
  </div>
);

export default React.memo(NewsTab);
